import React, { useLayoutEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import blogPosts from '../../data/blogPosts';
import './Blog.css';

const BLOG_HERO_TITLE_WORDS = ['Occasia', 'Journal'];
const BLOG_HERO_WORD_START = 0.12;
const BLOG_HERO_WORD_STAGGER = 0.1;
const BLOG_HERO_LEDE_DELAY =
  BLOG_HERO_WORD_START + (BLOG_HERO_TITLE_WORDS.length - 1) * BLOG_HERO_WORD_STAGGER + 0.15;
const BLOG_HERO_COPY_DURATION = 0.75;
const BLOG_HERO_SUBTITLE_DELAY = BLOG_HERO_LEDE_DELAY + 0.2;
const BLOG_HERO_CONTENT_DELAY = BLOG_HERO_SUBTITLE_DELAY + BLOG_HERO_COPY_DURATION;
const BLOG_HERO_VIDEO_URL = 'https://www.youtube.com/watch?v=6sKOnCaBWFE';
const BLOG_HERO_VIDEO_ID = '6sKOnCaBWFE';

const formatPostDate = (dateStr) =>
  new Date(dateStr).toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });

export const useSharedBlogPhotoSize = (containerRef) => {
  useLayoutEffect(() => {
    const root = containerRef.current;
    if (!root) return undefined;

    let lastSize = 0;
    const measure = () => {
      if (window.matchMedia('(max-width: 767px)').matches) {
        lastSize = 0;
        root.style.removeProperty('--blog-card-photo-size');
        return;
      }
      const copies = [...root.querySelectorAll('.blog-card-content')];
      const rootWidth = root.getBoundingClientRect().width;
      const columns = getComputedStyle(root).gridTemplateColumns;
      const columnCount =
        !columns || columns === 'none' ? 1 : columns.split(' ').filter(Boolean).length;
      const columnWidth = columnCount > 1 ? (rootWidth - 40) / columnCount : rootWidth;
      const cap = Math.max(120, Math.round(Math.min(280, columnWidth * 0.42)));
      const size = copies.reduce((max, node) => {
        const height = Math.round(node.getBoundingClientRect().height);
        return Math.max(max, Math.min(height, cap));
      }, 0);
      if (size > 0 && size !== lastSize) {
        lastSize = size;
        root.style.setProperty('--blog-card-photo-size', `${size}px`);
      }
    };

    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(root);
    root.querySelectorAll('.blog-card-content').forEach((node) => observer.observe(node));
    const onResize = () => {
      lastSize = 0;
      measure();
    };
    window.addEventListener('resize', onResize);
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', onResize);
    };
  }, [containerRef]);
};

export const BlogRow = ({ post }) => {
  return (
    <article className="blog-card">
      <Link to={`/blog/${post.slug}`} className="blog-card-image">
        <img src={post.heroImage} alt={post.title} loading="lazy" />
      </Link>
      <div className="blog-card-content">
        <div className="blog-card-tags">
          {post.tags.map((tag) => (
            <span key={tag}>{tag}</span>
          ))}
        </div>
        <Link to={`/blog/${post.slug}`} className="blog-card-title">
          <span>{post.title}</span>
        </Link>
        <p className="blog-card-excerpt">{post.excerpt}</p>
        <div className="blog-card-meta">
          <span>{formatPostDate(post.date)}</span>
          <span>•</span>
          <span>{post.readTime}</span>
        </div>
      </div>
    </article>
  );
};

const BlogList = () => {
  const gridRef = useRef(null);
  useSharedBlogPhotoSize(gridRef);

  return (
    <div className="blog-container">
      <div
        className="blog-inner"
        style={{ '--blog-content-delay': `${BLOG_HERO_CONTENT_DELAY}s` }}
      >
        <header className="blog-hero">
          <h1 className="blog-hero-title">
            {BLOG_HERO_TITLE_WORDS.map((word, wordIndex) => (
              <span
                key={`${word}-${wordIndex}`}
                className="blog-hero-word"
                style={{
                  animationDelay: `${BLOG_HERO_WORD_START + wordIndex * BLOG_HERO_WORD_STAGGER}s`,
                }}
              >
                {word}
              </span>
            ))}
          </h1>
          <p
            className="blog-hero-lede"
            style={{ animationDelay: `${BLOG_HERO_LEDE_DELAY}s` }}
          >
            We share what we learn while helping communities find the perfect spaces for their
            gatherings.
            <br />
            Practical, warm, and always community-first.
          </p>
          <div className="blog-hero-feature">
            <p
              className="blog-hero-subtitle"
              style={{ animationDelay: `${BLOG_HERO_SUBTITLE_DELAY}s` }}
            >
              Stories, playbooks, and data for independent venues & event organisers.
            </p>
            <a
              className="blog-hero-video"
              href={BLOG_HERO_VIDEO_URL}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Watch on YouTube"
            >
              <img
                src={`https://i.ytimg.com/vi/${BLOG_HERO_VIDEO_ID}/maxresdefault.jpg`}
                alt="Watch on YouTube"
              />
              <span className="blog-hero-video-play" aria-hidden>
                <span className="blog-hero-video-play-icon" />
              </span>
            </a>
          </div>
        </header>

        <section className="blog-insights">
          <h2 className="blog-insights-title">Our insights</h2>
          <div className="blog-grid" ref={gridRef}>
            {blogPosts.map((post) => (
              <BlogRow post={post} key={post.slug} />
            ))}
          </div>
        </section>
      </div>
    </div>
  );
};

export default BlogList;
